import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import api, {
  setToken,
  clearToken,
  getToken,
  emitSessionExpired,
  revokeSessionOnServer,
  exchangeLaunchToken,
  shouldSuppressSessionExpired,
  hasLaunchTokenInUrl,
  parseSessionUserFromToken,
  readSessionUserCache,
  writeSessionUserCache,
  clearSessionUserCache,
  stashLeaveWalletBalance,
  readRecentLeaveWalletBalance,
  clearLeaveWalletBalance,
} from 'lib/api';
import { initSessionUnloadHandling } from 'hooks/useExpireSessionOnTabClose';
import { connectGameSocket, disconnectGameSocket, setLeftTableResultHandler } from 'lib/socket';
import { API_ENDPOINTS } from 'constants/game';
import { SessionUser } from 'types/game';

interface RefreshSessionOptions {
  liveTableStack?: number;
  /** After leave: do not overwrite with a stale zero wallet from a racing session fetch */
  minExpectedWallet?: number;
  /** Use Redis-cached consumer wallet (no live user/detail) — for in-table stack sync */
  useCachedWallet?: boolean;
  /** Always call live consumer user/detail (e.g. home page after consumer launch) */
  forceLiveWallet?: boolean;
  /** Player left the table — ignore stale server tableStack in session snapshot */
  clearedTable?: boolean;
}

interface AuthContextValue {
  user: SessionUser | null;
  loading: boolean;
  /** Synchronous user snapshot — set before React re-render (avoids auth flash on launch). */
  getUserSnapshot: () => SessionUser | null;
  loginWithSession: (token: string) => Promise<boolean>;
  /** Exchange consumer launch token then store game session token. */
  exchangeAndLogin: (launchToken: string) => Promise<boolean>;
  devLogin: (username: string) => Promise<boolean>;
  refreshSession: (opts?: RefreshSessionOptions) => Promise<SessionUser | null>;
  /** Keep wallet header in sync with live table stack between session refreshes. */
  syncTableStack: (stack: number) => void;
  /** Apply wallet returned from leave_table (chips cashed out, nothing at table). */
  applyWalletAfterLeave: (walletBalance: number) => void;
  logout: () => void;
  /** Revoke server session, clear token, and redirect via SESSION_EXPIRED_EVENT */
  expireSession: () => void;
}

const AuthContext = createContext<AuthContextValue | null>(null);

function readCachedSessionUser(): SessionUser | null {
  return readSessionUserCache();
}

function writeCachedSessionUser(user: SessionUser): void {
  writeSessionUserCache(user);
}

/** Consumer full-wallet — one balance field; tableStack is rejoin metadata only. */
function mergeConsumerSession(
  data: SessionUser,
  opts?: RefreshSessionOptions,
  prev?: SessionUser | null
): SessionUser {
  let balance = Math.max(0, Math.floor(Number(data.balance) || 0));
  let tableStack = Math.max(0, Math.floor(Number(data.tableStack) || 0));
  let activeTableProfileId = data.activeTableProfileId;

  if (opts?.clearedTable) {
    tableStack = 0;
    activeTableProfileId = undefined;
  }

  if (opts?.liveTableStack != null) {
    const live = Math.max(0, Math.floor(Number(opts.liveTableStack) || 0));
    balance = live;
    tableStack = live;
  }

  if (opts?.minExpectedWallet != null && opts.minExpectedWallet > 0) {
    const expected = Math.floor(opts.minExpectedWallet);
    if (balance <= 0) {
      balance = expected;
    } else if (opts?.clearedTable && balance > expected) {
      balance = expected;
    }
    return {
      ...data,
      balance,
      lockedBalance: 0,
      tableStack: 0,
      activeTableProfileId: undefined,
      totalBalance: balance,
      consumerBalanceStale: false,
    };
  }

  if (data.consumerBalanceStale && prev && !opts?.forceLiveWallet && opts?.liveTableStack == null) {
    const prevBalance = Math.max(0, Math.floor(Number(prev.balance) || 0));
    if (prevBalance > balance) balance = prevBalance;
    const prevStack = Math.max(0, Math.floor(Number(prev.tableStack) || 0));
    if (prevStack > tableStack) {
      tableStack = prevStack;
      activeTableProfileId = activeTableProfileId ?? prev.activeTableProfileId;
    }
  }

  if (balance === 0 && tableStack === 0 && prev && (prev.balance ?? 0) > 0 && !opts?.forceLiveWallet) {
    balance = Math.max(0, Math.floor(Number(prev.balance) || 0));
  }

  return {
    ...data,
    balance,
    lockedBalance: 0,
    tableStack,
    activeTableProfileId,
    totalBalance: balance,
    consumerBalanceStale: data.consumerBalanceStale,
  };
}

function mergeSessionUser(
  data: SessionUser,
  opts?: RefreshSessionOptions,
  prev?: SessionUser | null
): SessionUser {
  if (data.consumerWallet) {
    return mergeConsumerSession(data, opts, prev);
  }

  let wallet = Math.max(0, Math.floor(Number(data.balance) || 0));
  let locked = Math.max(0, Math.floor(Number(data.lockedBalance) || 0));
  let tableStack = Math.max(0, Math.floor(Number(data.tableStack) || 0));
  let activeTableProfileId = data.activeTableProfileId;

  if (opts?.clearedTable || (opts?.minExpectedWallet != null && opts.minExpectedWallet > 0)) {
    tableStack = 0;
    activeTableProfileId = undefined;
    locked = 0;
  }

  if (opts?.liveTableStack != null) {
    tableStack = Math.max(0, Math.floor(Number(opts.liveTableStack) || 0));
  }

  if (
    prev &&
    (prev.tableStack ?? 0) === 0 &&
    opts?.liveTableStack == null &&
    tableStack > 0 &&
    (prev.balance ?? 0) > 0 &&
    opts?.clearedTable !== true
  ) {
    tableStack = 0;
    activeTableProfileId = undefined;
  }

  if (opts?.minExpectedWallet != null && opts.minExpectedWallet > 0) {
    const expected = Math.floor(opts.minExpectedWallet);
    if (wallet <= 0) wallet = expected;
    locked = 0;
    tableStack = 0;
    activeTableProfileId = undefined;
    return {
      ...data,
      balance: wallet,
      lockedBalance: locked,
      tableStack,
      activeTableProfileId,
      totalBalance: wallet,
      consumerBalanceStale: false,
    };
  }

  if (data.consumerBalanceStale && prev && !opts?.forceLiveWallet) {
    const prevWallet = Math.max(0, Math.floor(Number(prev.balance) || 0));
    const prevLocked = Math.max(0, Math.floor(Number(prev.lockedBalance) || 0));
    const prevStack = Math.max(0, Math.floor(Number(prev.tableStack) || 0));
    tableStack = opts?.liveTableStack != null ? tableStack : Math.max(tableStack, prevStack);

    if ((prev.tableStack ?? 0) === 0 && tableStack > 0 && (prev.balance ?? 0) > 0) {
      tableStack = 0;
      activeTableProfileId = undefined;
    }

    const effectiveWallet = wallet > 0 ? wallet : prevWallet;
    const totalBalance =
      tableStack > 0 ? effectiveWallet + tableStack : effectiveWallet + prevLocked;
    return {
      ...data,
      balance: effectiveWallet,
      lockedBalance: prevLocked,
      tableStack,
      activeTableProfileId: tableStack > 0 ? activeTableProfileId ?? prev.activeTableProfileId : undefined,
      totalBalance,
      consumerBalanceStale: true,
    };
  }

  if (opts?.liveTableStack != null) {
    const totalBalance = tableStack > 0 ? wallet + tableStack : wallet + locked;
    return {
      ...data,
      balance: wallet,
      lockedBalance: locked,
      tableStack,
      activeTableProfileId,
      totalBalance,
      consumerBalanceStale: data.consumerBalanceStale,
    };
  }

  const totalBalance = tableStack > 0 ? wallet + tableStack : wallet + locked;

  if (wallet === 0 && tableStack === 0 && prev && (prev.balance ?? 0) > 0 && !opts?.forceLiveWallet) {
    return {
      ...data,
      balance: Math.max(0, Math.floor(Number(prev.balance) || 0)),
      lockedBalance: 0,
      tableStack: 0,
      activeTableProfileId: undefined,
      totalBalance: Math.max(0, Math.floor(Number(prev.balance) || 0)),
      consumerBalanceStale: data.consumerBalanceStale,
    };
  }

  return {
    ...data,
    balance: wallet,
    lockedBalance: locked,
    tableStack,
    activeTableProfileId,
    totalBalance,
    consumerBalanceStale: data.consumerBalanceStale,
  };
}

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const userRef = useRef<SessionUser | null>(
    hasLaunchTokenInUrl() ? null : readCachedSessionUser()
  );
  const refreshInflightRef = useRef<Promise<SessionUser | null> | null>(null);
  const [user, setUserState] = useState<SessionUser | null>(() => userRef.current);
  const [loading, setLoading] = useState(true);

  const setUser = useCallback((next: SessionUser | null | ((prev: SessionUser | null) => SessionUser | null)) => {
    setUserState((prev) => {
      const resolved = typeof next === 'function' ? next(prev) : next;
      userRef.current = resolved;
      return resolved;
    });
  }, []);

  const getUserSnapshot = useCallback(() => userRef.current, []);

  const refreshSession = useCallback(async (opts?: RefreshSessionOptions): Promise<SessionUser | null> => {
    if (!getToken()) {
      if (!shouldSuppressSessionExpired()) {
        setUser(null);
      }
      return null;
    }

    if (!navigator.onLine) {
      const cached = readCachedSessionUser();
      const keep = userRef.current ?? cached;
      if (keep) {
        setUser((prev) => prev ?? keep);
        return keep;
      }
      return null;
    }

    const recentLeaveWallet = readRecentLeaveWalletBalance();
    const effectiveOpts: RefreshSessionOptions = { ...opts };
    if (recentLeaveWallet != null && effectiveOpts.minExpectedWallet == null) {
      effectiveOpts.clearedTable = true;
      if (recentLeaveWallet > 0) {
        effectiveOpts.minExpectedWallet = recentLeaveWallet;
      }
    }

    const forceRefresh =
      effectiveOpts?.minExpectedWallet != null || effectiveOpts?.forceLiveWallet === true;

    const needsLiveWallet =
      effectiveOpts?.forceLiveWallet === true ||
      (!effectiveOpts?.useCachedWallet &&
        effectiveOpts?.minExpectedWallet == null &&
        !effectiveOpts?.liveTableStack);

    if (!forceRefresh && !needsLiveWallet && refreshInflightRef.current) {
      return refreshInflightRef.current;
    }

    const sessionPath = effectiveOpts?.forceLiveWallet
      ? `${API_ENDPOINTS.FRONTEND.SESSION}?wallet=force`
      : !effectiveOpts?.forceLiveWallet &&
          (effectiveOpts?.useCachedWallet || effectiveOpts?.liveTableStack != null)
        ? `${API_ENDPOINTS.FRONTEND.SESSION}?wallet=cached`
        : API_ENDPOINTS.FRONTEND.SESSION;

    const run = (async (): Promise<SessionUser | null> => {
      const res = await api.get<SessionUser>(sessionPath);
      if (res.success && res.data) {
        let mergedResult: SessionUser | null = null;
        setUser((prev) => {
          const merged = mergeSessionUser(res.data!, effectiveOpts, prev);
          const leaveWallet = readRecentLeaveWalletBalance();
          if (
            leaveWallet != null &&
            prev &&
            (prev.tableStack ?? 0) === 0 &&
            (prev.balance ?? 0) > 0 &&
            (merged.balance ?? 0) <= 0 &&
            (merged.tableStack ?? 0) === 0
          ) {
            mergedResult = prev;
            return prev;
          }
          if (
            effectiveOpts?.minExpectedWallet == null &&
            effectiveOpts?.forceLiveWallet !== true &&
            prev &&
            (prev.balance ?? 0) > 0 &&
            (merged.balance ?? 0) === 0 &&
            (merged.tableStack ?? 0) === 0
          ) {
            mergedResult = prev;
            return prev;
          }
          writeCachedSessionUser(merged);
          mergedResult = merged;
          return merged;
        });
        if (effectiveOpts?.minExpectedWallet != null && effectiveOpts.minExpectedWallet > 0) {
          clearLeaveWalletBalance();
        }
        return mergedResult;
      }

      if (res.error_code === '100') {
        if (shouldSuppressSessionExpired()) {
          const cached = readCachedSessionUser();
          return userRef.current ?? cached;
        }
        clearToken();
        clearSessionUserCache();
        setUser(null);
        disconnectGameSocket();
        emitSessionExpired({ force: true });
        return null;
      }

      const cached = readCachedSessionUser();
      const keep = userRef.current ?? cached;
      if (keep) {
        setUser((prev) => prev ?? keep);
        return keep;
      }
      return null;
    })();

    refreshInflightRef.current = run;
    try {
      return await run;
    } finally {
      if (refreshInflightRef.current === run) {
        refreshInflightRef.current = null;
      }
    }
  }, [setUser]);

  useEffect(() => {
    initSessionUnloadHandling();
    if (hasLaunchTokenInUrl()) {
      userRef.current = null;
      setUserState(null);
      setLoading(true);
      return;
    }
    if (getToken()) {
      refreshSession().finally(() => setLoading(false));
    } else {
      setLoading(false);
    }
  }, [refreshSession, setUser]);

  /** Keep game socket connected while logged in so session_superseded kicks reach the lobby. */
  useEffect(() => {
    if (!user || !getToken()) return;
    connectGameSocket();
  }, [user]);

  /** Detect login on another device — poll session while app is visible. */
  useEffect(() => {
    if (!user || !getToken()) return;

    const checkSession = () => {
      if (document.visibilityState !== 'visible') return;
      if (!navigator.onLine) return;
      if (!getToken()) return;
      void refreshSession({ useCachedWallet: true });
    };

    const onOnline = () => {
      if (!getToken()) return;
      void refreshSession({ useCachedWallet: true });
      connectGameSocket();
    };

    const onVisible = () => checkSession();
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('online', onOnline);
    const interval = window.setInterval(checkSession, 30_000);

    return () => {
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('online', onOnline);
      window.clearInterval(interval);
    };
  }, [user, refreshSession]);

  const exchangeAndLogin = useCallback(async (launchToken: string) => {
    setLoading(true);
    setUser(null);
    try {
      const res = await exchangeLaunchToken(launchToken);
      if (!res.success || !res.data?.token) {
        clearToken();
        clearSessionUserCache();
        setUser(null);
        return false;
      }
      setToken(res.data.token);
      if (res.data.session) {
        const merged = mergeSessionUser(res.data.session);
        userRef.current = merged;
        setUserState(merged);
        writeCachedSessionUser(merged);
        return true;
      }
      let sessionUser = await refreshSession();
      if (!sessionUser) {
        sessionUser =
          readCachedSessionUser() ?? parseSessionUserFromToken(res.data.token);
      }
      if (sessionUser) {
        userRef.current = sessionUser;
        setUserState(sessionUser);
        writeCachedSessionUser(sessionUser);
      }
      return getToken() != null && sessionUser != null;
    } catch {
      clearToken();
      clearSessionUserCache();
      setUser(null);
      return false;
    } finally {
      setLoading(false);
    }
  }, [refreshSession, setUser]);

  const loginWithSession = useCallback(async (token: string) => {
    setToken(token);
    setLoading(true);
    try {
      const sessionUser = await refreshSession();
      return sessionUser != null;
    } catch {
      clearToken();
      setUser(null);
      return false;
    } finally {
      setLoading(false);
    }
  }, [refreshSession, setUser]);

  const devLogin = useCallback(async (username: string) => {
    setLoading(true);
    try {
      const res = await api.post<{ token: string }>(API_ENDPOINTS.FRONTEND.DEV_LOGIN, { username });
      if (res.success && res.data?.token) {
        setToken(res.data.token);
        await refreshSession();
        return true;
      }
      return false;
    } finally {
      setLoading(false);
    }
  }, [refreshSession]);

  const syncTableStack = useCallback((stack: number) => {
    setUser((prev) => {
      if (!prev) return prev;
      const chips = Math.max(0, Math.floor(Number(stack) || 0));
      if (prev.consumerWallet) {
        const next = {
          ...prev,
          balance: chips,
          lockedBalance: 0,
          tableStack: chips,
          totalBalance: chips,
          consumerBalanceStale: false,
        };
        writeCachedSessionUser(next);
        return next;
      }
      const wallet = Math.max(0, Math.floor(Number(prev.balance) || 0));
      const next = {
        ...prev,
        tableStack: chips,
        totalBalance: chips > 0 ? wallet + chips : wallet,
        consumerBalanceStale: false,
      };
      writeCachedSessionUser(next);
      return next;
    });
  }, [setUser]);

  const applyWalletAfterLeave = useCallback((walletBalance: number) => {
    const wallet = Math.max(0, Math.floor(Number(walletBalance) || 0));
    stashLeaveWalletBalance(wallet);
    setUser((prev) => {
      if (!prev) return prev;
      const next: SessionUser = {
        ...prev,
        balance: wallet,
        lockedBalance: 0,
        tableStack: 0,
        activeTableProfileId: undefined,
        totalBalance: wallet,
        consumerBalanceStale: false,
      };
      writeCachedSessionUser(next);
      return next;
    });
  }, [setUser]);

  useEffect(() => {
    setLeftTableResultHandler((payload) => {
      const remaining = Number(payload.remainingStack);
      const cashOut = Number(payload.cashOut);
      const reported = Number(payload.walletBalance);
      const walletBalance = Math.max(
        0,
        Math.floor(
          Number.isFinite(remaining)
            ? remaining
            : Number.isFinite(cashOut)
              ? cashOut
              : Number.isFinite(reported)
                ? reported
                : 0
        )
      );
      applyWalletAfterLeave(walletBalance);
      void refreshSession({
        forceLiveWallet: true,
        clearedTable: true,
        minExpectedWallet: walletBalance > 0 ? walletBalance : undefined,
      });
    });
    return () => setLeftTableResultHandler(null);
  }, [applyWalletAfterLeave, refreshSession]);

  const logout = useCallback(() => {
    clearToken();
    setUser(null);
    disconnectGameSocket();
  }, [setUser]);

  const expireSession = useCallback(() => {
    const token = getToken();
    if (token) revokeSessionOnServer(token);
    clearToken();
    setUser(null);
    disconnectGameSocket();
    emitSessionExpired();
  }, [setUser]);

  const value = useMemo(
    () => ({
      user,
      loading,
      getUserSnapshot,
      loginWithSession,
      exchangeAndLogin,
      devLogin,
      refreshSession,
      syncTableStack,
      applyWalletAfterLeave,
      logout,
      expireSession,
    }),
    [user, loading, getUserSnapshot, loginWithSession, exchangeAndLogin, devLogin, refreshSession, syncTableStack, applyWalletAfterLeave, logout, expireSession]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
